import React, { FunctionComponent, Fragment, useContext, useCallback } from "react";
import { Grid } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { LoginFormButton } from "./form-components/login-form-button";
import { LoginFormTextField } from "./form-components/login-form-textfield";
import { useEmail } from "../../hooks/use-email";
import { LoginContext } from "./context/login-context";
import { LOGIN_FORM_TYPE } from "../../models/login-form-type";
import { useResetPasswordMutation } from "../../api/smartmelts-api/graphql/generated";
import { SnackbarSeverity, useSnackbar } from "../../components/snackbar/snackbar-context";
import { getGraphqlErrorLocalized } from "../../api/errors/graphql-error-handler";
import { useEnterCallback } from "../../hooks/use-enter-callback";

interface IPasswordForgottenFormProps {}

export const PasswordForgottenForm: FunctionComponent<IPasswordForgottenFormProps> = (props) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { setFormType, toggleShakeAnimation } = useContext(LoginContext);
  const { email, isEmailValid, onEmailChange } = useEmail("");

  const [resetPasswordMutation, { loading }] = useResetPasswordMutation({
    onCompleted: () => {
      setFormType(LOGIN_FORM_TYPE.LOGIN);
      showSnackbar(t("login.reset_password_info"));
    },
    onError: (error) => {
      showSnackbar(getGraphqlErrorLocalized(t, "login", error), SnackbarSeverity.ERROR);
    },
  });

  const onClickReset = useCallback(() => {
    if (isEmailValid) {
      resetPasswordMutation({ variables: { email } });
    } else {
      toggleShakeAnimation();
    }
  }, [resetPasswordMutation, email, isEmailValid, toggleShakeAnimation]);

  const onKeyDown = useEnterCallback(onClickReset);

  return (
    <Fragment>
      <Grid container direction="column">
        <LoginFormTextField
          label={t("login.fields.email")}
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          error={!isEmailValid}
          onKeyDown={onKeyDown}
        />
      </Grid>
      <Grid container justify="center">
        <LoginFormButton onClick={onClickReset} loading={loading}>
          {t("login.reset_password_action")}
        </LoginFormButton>
      </Grid>
    </Fragment>
  );
};
